import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Profile } from '../lib/database.types';
import * as profileService from '../services/profileService';
import { logger } from '../lib/logger';

const log = logger.createNamespace('useProfile');

interface UseProfileResult {
  profile: Profile | null;
  loading: boolean;
  error: string | null;
  updateProfile: (updates: Partial<Profile>) => Promise<{ error: Error | null }>;
  refetch: () => Promise<void>;
}

export const useProfile = (): UseProfileResult => {
  const { user } = useAuth();
  const [profile, setProfile] = useState<Profile | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchProfile = useCallback(async () => {
    if (!user) {
      setProfile(null);
      setLoading(false);
      return;
    }

    log.debug('Fetching profile', { userId: user.id });

    try {
      setLoading(true);
      setError(null);

      const data = await profileService.getProfile();
      log.debug('Profile loaded', { onboardingCompleted: data?.onboarding_completed });
      setProfile(data);
    } catch (err) {
      log.error('Failed to fetch profile', err);
      setError(err instanceof Error ? err.message : 'Failed to fetch profile');
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    fetchProfile();
  }, [fetchProfile]);

  const updateProfile = useCallback(async (updates: Partial<Profile>) => {
    if (!user) {
      log.error('Cannot update profile: not authenticated');
      return { error: new Error('Not authenticated') };
    }

    log.debug('Updating profile', { updates });

    try {
      const data = await profileService.updateProfile(updates);
      log.info('Profile updated successfully');
      setProfile(data);
      return { error: null };
    } catch (err) {
      log.error('Profile update exception', err);
      return { error: err as Error };
    }
  }, [user]);

  return {
    profile,
    loading,
    error,
    updateProfile,
    refetch: fetchProfile,
  };
};
